/**
 * Singleton AudioContext for the entire app.
 * Chrome's autoplay policy requires AudioContext.resume() to be called
 * in a user gesture. We keep one context alive and resume it on every
 * click/keydown/touchstart so it never stays suspended.
 */

let _ctx: AudioContext | null = null;
let _unlockBound = false;

function _unlock() {
  if (!_ctx) return;
  if (_ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
}

function _bindUnlockListeners() {
  if (_unlockBound) return;
  _unlockBound = true;
  document.addEventListener('click',      _unlock, { passive: true });
  document.addEventListener('keydown',    _unlock, { passive: true });
  document.addEventListener('touchstart', _unlock, { passive: true });
  document.addEventListener('pointerdown',_unlock, { passive: true });
}

/**
 * Returns the shared AudioContext if it already exists, without creating one.
 * Safe to call from mouseenter / passive events.
 */
export function peekAudioCtx(): AudioContext | null {
  if (!_ctx || _ctx.state === 'closed') return null;
  return _ctx;
}

/**
 * Returns the shared AudioContext, creating it if needed.
 * MUST be called from a trusted user gesture (click / keydown / touchstart).
 */
export function getAudioCtx(): AudioContext | null {
  if (_ctx && _ctx.state !== 'closed') return _ctx;
  try {
    _ctx = new AudioContext();
  } catch {
    return null;
  }
  _bindUnlockListeners();
  // Attempt immediate resume (works when called inside a user gesture)
  if (_ctx.state === 'suspended') {
    _ctx.resume().catch(() => {});
  }
  return _ctx;
}

/** Call this inside any user-gesture handler to ensure the context is running. */
export function resumeAudioCtx() {
  _unlock();
}
